import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import clsx from 'clsx';
import CloseIcon from '../Icons/CloseIcon';

const Transition = React.forwardRef(function Transition(props, ref) {
  // eslint-disable-next-line react/prop-types
  return <Slide direction={props.direction} ref={ref} {...props} />;
});

const SLIDE_DIRECTION = {
  left: 'right',
  right: 'left',
  center: 'up',
};

const StyledDialog = styled(Dialog)`
  .MuiDialog-container {
    position: relative;
    .MuiPaper-root {
      position: absolute;
      margin: 0;
      
      ${({ $hasContentFullWidth }) =>
        $hasContentFullWidth
          ? css`
              width: 100%;
            `
          : css``}
      
      ${({ $alignment }) => {
        switch ($alignment) {
          case 'left':
            return `border-radius: 0; left: 0;`;
          case 'right':
            return `border-radius: 0; right: 0;`;
          default:
            return ``;
        }
      }}
      ${({ $fullWidth }) =>
        $fullWidth && `max-width: none; width: initial; left: 0; right: 0;`}
      ${({ $fullHeight }) =>
        $fullHeight && `max-height: none; height: initial; top: 0; bottom: 0;`}
    }

    .MuiDialogContent-root {
      ${({ $hasContentFullWidth }) =>
        $hasContentFullWidth
          ? css`
              width: 100%;
              overflow-x: hidden;
            `
          : css``}
    }
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 24px;
  border-bottom: 1px solid
    ${({ theme }) => theme.palette.themeCommon.black.light};

  .MuiTypography-root {
    display: flex;
    align-items: center;
    font-size: 1rem;
  }

  .modal-title {
    width: 100%;
  }
`;

const StyledIconButton = styled(IconButton)`
  padding: 0;
  margin-left: auto;
`;

const StyledDialogContent = styled(DialogContent)`
  padding: ${({ padding }) => padding || '8px 56px 8px 24px'};

  &:not(.hasTitle) {
    padding: ${({ $withNoTitleContentPadding }) =>
      $withNoTitleContentPadding} !important;
  }
`;

const StyledDialogActions = styled(DialogActions)`
  justify-content: left;
  padding: 24px 63px 24px 40px;
  border-top: 1px solid ${({ theme }) => theme.palette.themeCommon.black.light};

  &:not(.hasTitle) {
    width: 100%;
    padding: 24px;
  }

  & > *:not(:first-child) {
    margin-left: 16px;
  }
`;

function Modal({
  openModal,
  onModalClose,
  fromRoutePath,
  alignment,
  fullWidth,
  fullHeight,
  showCloseIcon,
  modalLabeledBy,
  modalDescribedBy,
  title: Title,
  children: Content,
  actions: Actions,
  showTitle,
  history,
  className,
  disableEscapeKeyDown,
  onSyncModalClose,
  withNoTitleContentPadding,
  closeModalFunctionRef,
  historyState,
  contentRef,
  hasContentFullWidth,
  padding,
  ...rest
}) {
  const isRouteModal = Boolean(fromRoutePath);
  const [open, setOpen] = useState(isRouteModal);
  const openValue = isRouteModal ? open : openModal;

  const handleClose = (...args) => {
    if (isRouteModal) {
      setOpen(false);
      setTimeout(() => {
        // if fromPreviousPath is a history callback, call it
        if (typeof fromRoutePath === 'function') {
          fromRoutePath();
          return;
        }

        history.push(fromRoutePath, historyState || null);

        onSyncModalClose(...args);
      }, 150);
    }
    onModalClose(...args);
  };

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    closeModalFunctionRef.current = handleClose;
  }, [isRouteModal, fromRoutePath]);

  return (
    <StyledDialog
      $hasContentFullWidth={hasContentFullWidth}
      open={openValue}
      TransitionComponent={Transition}
      TransitionProps={{ direction: SLIDE_DIRECTION[alignment] }}
      aria-labelledby={modalLabeledBy}
      aria-describedby={modalDescribedBy}
      $alignment={alignment}
      $fullWidth={fullWidth}
      $fullHeight={fullHeight}
      className={className}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      {showTitle ? (
        <StyledDialogTitle>
          <div className="modal-title">
            {typeof Title === 'function' ? <Title /> : Title}
          </div>
          {showCloseIcon && (
            <StyledIconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </StyledIconButton>
          )}
        </StyledDialogTitle>
      ) : null}
      <StyledDialogContent
        padding={padding}
        $withNoTitleContentPadding={withNoTitleContentPadding}
        className={clsx({ hasTitle: showTitle })}
        ref={contentRef}
      >
        {typeof Content === 'function' ? (
          <Content closeRouteModal={handleClose} history={history} {...rest} />
        ) : (
          Content
        )}
      </StyledDialogContent>
      {Actions && (
        <StyledDialogActions className={showTitle && 'hasTitle'}>
          <Actions closeRouteModal={handleClose} />
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
}

Modal.propTypes = {
  alignment(props, propName) {
    const propValue = props[propName];
    const options = ['left', 'center', 'right'];
    if (!options.includes(propValue)) {
      throw new Error(`${propName} must be one of ${options.toString()}`);
    }
  },
  fullWidth: PropTypes.bool,
  fullHeight: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  showTitle: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType])
    .isRequired,
  actions: PropTypes.func,
  fromRoutePath(props, propName) {
    const prop = props[propName];
    if (!['string', 'function'].includes(typeof prop)) {
      throw new Error('fromRoutePath must be either a string or a function!');
    }
    if (prop && !props.history) {
      throw new Error('history is required if fromRoutePath is present!');
    }
  },
  openModal: PropTypes.bool,
  onModalClose: PropTypes.func,
  history: PropTypes.shape({ push: PropTypes.func }),
  modalLabeledBy: PropTypes.string,
  modalDescribedBy: PropTypes.string,
  className: PropTypes.string,
  disableEscapeKeyDown: PropTypes.bool,
  onSyncModalClose: PropTypes.func,
  withNoTitleContentPadding: PropTypes.string,
  closeModalFunctionRef: PropTypes.shape({
    current: PropTypes.func,
  }),
  historyState: PropTypes.instanceOf(Object),
  // eslint-disable-next-line react/forbid-prop-types
  contentRef: PropTypes.shape({ current: PropTypes.object }),
  hasContentFullWidth: PropTypes.bool,
  padding: PropTypes.string,
};

Modal.defaultProps = {
  actions: null,
  alignment: 'left',
  fullWidth: false,
  fullHeight: true,
  showCloseIcon: true,
  fromRoutePath: '',
  openModal: false,
  onModalClose: () => null,
  history: null,
  modalLabeledBy: 'modal-title',
  modalDescribedBy: 'modal-description',
  showTitle: true,
  title: '',
  className: '',
  disableEscapeKeyDown: false,
  onSyncModalClose: () => {},
  withNoTitleContentPadding: '32px 24px',
  closeModalFunctionRef: {
    current: () => {},
  },
  historyState: null,
  contentRef: { current: null },
  hasContentFullWidth: false,
  padding: '',
};

export default Modal;
